import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import EmailValidator from './components/EmailValidator';
import SecurityMessage from './components/SecurityMessage';
import AlternativeOptions from './components/AlternativeOptions';
import SuccessMessage from './components/SuccessMessage';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    setError,
    clearErrors
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: ''
    }
  });

  const watchedEmail = watch('email');

  const handleResetPassword = async (data) => {
    setIsLoading(true);
    clearErrors();

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // For demo purposes, simulate different responses
      if (data.email === 'notfound@example.com') {
        setError('email', {
          type: 'manual',
          message: 'No account found with this email address'
        });
        setIsLoading(false);
        return;
      }

      if (data.email === 'blocked@example.com') {
        setError('email', {
          type: 'manual',
          message: 'This account is temporarily blocked. Please contact support.'
        });
        setIsLoading(false);
        return;
      }

      // Success case
      setSubmittedEmail(data.email);
      setIsSuccess(true);

    } catch (error) {
      console.error('Reset password error:', error);
      setError('email', {
        type: 'manual',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleTryAgain = () => {
    setIsSuccess(false);
    setSubmittedEmail('');
  };

  if (isSuccess) {
    return (
      <SuccessMessage
        email={submittedEmail}
        onTryAgain={handleTryAgain}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/Giftsy-Logo-Final-1751671984839.png"
              alt="Giftsy Modern"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl font-heading font-bold text-accent-800 mb-2">
            Reset Your Password
          </h1>
          <p className="text-accent-600 text-sm max-w-sm mx-auto">
            Don't worry, it happens to the best of us. Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Reset Form Card */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <form onSubmit={handleSubmit(handleResetPassword)} className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                placeholder="Enter your email address"
                className={errors.email ? 'border-error' : ''}
                autoComplete="email"
                autoFocus
              />
              {errors.email && (
                <p className="text-error text-sm mt-1">{errors.email.message}</p>
              )}
              <EmailValidator email={watchedEmail} />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!isValid || isLoading}
              loading={isLoading}
              fullWidth
              variant="primary"
              size="lg"
              className="mt-6"
            >
              {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
            </Button>
          </form>

          {/* Security Message */}
          <SecurityMessage />

          {/* Alternative Options */}
          <AlternativeOptions />

          {/* Back to Login */}
          <div className="text-center mt-6">
            <button
              onClick={handleBackToLogin}
              className="text-accent-600 hover:text-primary-600 text-sm font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center text-xs text-accent-500 max-w-sm mx-auto">
          <p>
            If you don't receive an email within a few minutes, check your spam folder or{' '}
            <a href="/contact" className="text-primary hover:text-primary-600 font-medium">
              contact our support team
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;